// src/pages/Users/Inscription/AdherentCard.jsx

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaBell,
  FaCalendar,
  FaCalendarAlt,
  FaEuroSign,
  FaFileMedical,
  FaFilePdf,
  FaFileSignature,
  FaGoogleDrive,
  FaIdCard,
  FaImage,
  FaLink,
  FaLock,
  FaPlusCircle,
  FaThumbsUp,
  FaTimesCircle,
  FaUserCircle,
  FaUsers,
} from "react-icons/fa";

import ValidateTrialModal from "./ValidateTrialModal";
import EditDocumentsModal from "./EditDocumentsModal";

import { getSettings } from "../../../services/settingsServices";
import {
  getReglementInscriptionByIdAndSaison,
  updateReglementInscription,
} from "../../../services/reglementinscriptionServices";
import { sendNotificationToAdmin } from "../../../services/notificationServices";

/* ---------- Helpers ---------- */

const toBooleanFlag = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();

    return ["1", "true", "yes", "oui", "validé", "valide"].includes(
      normalized,
    );
  }

  return Boolean(value);
};

/* ---------- Helpers URL / documents ---------- */

const toPublicUrl = (url) => {
  if (!url) return "";

  if (
    /^https?:\/\//i.test(url) ||
    url.startsWith("data:") ||
    url.startsWith("blob:")
  ) {
    return url;
  }

  if (!url.startsWith("/uploads/")) {
    return url;
  }

  const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

  return `${baseUrl}${url}`;
};

const isDrive = (url) =>
  !!url &&
  (url.includes("drive.google.com") ||
    url.includes("docs.google.com") ||
    url.includes("googleusercontent.com"));

const isImage = (url) =>
  !!url && /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url.split("?")[0]);

const isPdf = (url) =>
  !!url &&
  (url.toLowerCase().endsWith(".pdf") ||
    url.toLowerCase().includes("pdf"));

/* ---------- Bouton document ---------- */

const BigDocButton = ({ url, onOpen, label }) => {
  const hasDocument = Boolean(url);

  let Icon = FaTimesCircle;
  let color = "text-slate-300";
  let documentType = "Aucun document";

  if (hasDocument) {
    if (isDrive(url)) {
      Icon = FaGoogleDrive;
      color = "text-sky-600";
      documentType = "Lien Google Drive";
    } else if (isPdf(url)) {
      Icon = FaFilePdf;
      color = "text-violet-600";
      documentType = "Fichier PDF";
    } else if (isImage(url)) {
      Icon = FaImage;
      color = "text-emerald-600";
      documentType = "Image";
    } else {
      Icon = FaLink;
      color = "text-slate-600";
      documentType = "Lien";
    }
  }

  return (
    <button
      type="button"
      onClick={hasDocument ? onOpen : undefined}
      disabled={!hasDocument}
      className={`group flex w-full flex-col items-center justify-center rounded-xl border border-dashed p-4 transition ${
        hasDocument
          ? "border-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          : "cursor-not-allowed border-slate-300 bg-slate-50/50 dark:bg-slate-900/30"
      }`}
      title={hasDocument ? "Ouvrir le document" : "Aucun document"}
    >
      <Icon
        className={`mb-1.5 text-4xl transition ${color} ${
          hasDocument ? "group-hover:scale-105" : ""
        }`}
      />

      <div className="text-xs font-medium text-slate-700 dark:text-slate-200">
        {label}
      </div>

      <div
        className={`text-[11px] ${
          hasDocument ? "text-slate-500" : "text-slate-400"
        }`}
      >
        {documentType}
        {hasDocument && " — cliquer pour ouvrir"}
      </div>
    </button>
  );
};

/* ---------- Section ---------- */

const Section = ({ title, icon, validated, children }) => {
  const backgroundClass = validated
    ? "bg-emerald-50 dark:bg-emerald-900/20"
    : "bg-slate-50 dark:bg-slate-900/40";

  const ringClass = validated
    ? "ring-emerald-200/70 dark:ring-emerald-800/40"
    : "ring-slate-200/70 dark:ring-slate-800/70";

  return (
    <div
      className={`rounded-2xl p-4 ring-1 ${backgroundClass} ${ringClass}`}
    >
      <div className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
        <span className="text-base">{icon}</span>

        <h4 className="text-sm font-semibold sm:text-base">{title}</h4>
      </div>

      <div className="mt-3">{children}</div>
    </div>
  );
};

/* ---------- Composant ---------- */

const AdherentCard = ({
  adherent,
  dossier,
  user,
  refreshAdherents,
  refreshDossier,
}) => {
  const navigate = useNavigate();

  const [activeSeason, setActiveSeason] = useState("");
  const [settings, setSettings] = useState(null);
  const [reglement, setReglement] = useState(null);

  const [isTrialOpen, setIsTrialOpen] = useState(false);
  const [isDocsOpen, setIsDocsOpen] = useState(false);

  const [editingType, setEditingType] = useState(false);
  const [typeReglementLocal, setTypeReglementLocal] = useState("");
  const [savingType, setSavingType] = useState(false);

  const defaultImageUrl =
    adherent?.genre === "Masculin"
      ? "/img/judoka_avatar.png"
      : "/img/judokate_avatar.png";

  /* ---------- Settings et saison ---------- */

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getSettings();

        if (Array.isArray(data) && data[0]) {
          setSettings(data[0]);
          setActiveSeason(data[0].saison_active || "");
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);
      }
    };

    fetchSettings();
  }, []);

  /* ---------- Règlement courant ---------- */

  const refetchReglement = async () => {
    if (!activeSeason || !adherent?.id) return;

    try {
      const data = await getReglementInscriptionByIdAndSaison(
        adherent.id,
        activeSeason,
      );

      setReglement(data || null);
    } catch (error) {
      console.error(
        "Erreur lors de l’actualisation du règlement :",
        error,
      );
    }
  };

  useEffect(() => {
    const fetchReglement = async () => {
      if (!activeSeason || !adherent?.id) return;

      try {
        const data = await getReglementInscriptionByIdAndSaison(
          adherent.id,
          activeSeason,
        );

        setReglement(data || null);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération du règlement :",
          error,
        );
      }
    };

    fetchReglement();
  }, [adherent?.id, activeSeason]);

  useEffect(() => {
    setTypeReglementLocal(reglement?.type_reglement || "");
  }, [reglement]);

  if (!adherent) {
    return null;
  }

  if (!dossier) {
    return (
      <div className="rounded-2xl bg-white/80 p-4 ring-1 ring-slate-200 dark:bg-slate-900/60 dark:ring-slate-700">
        Pas de dossier d’inscription pour {adherent.nom} {adherent.prenom}.
      </div>
    );
  }

  /* ---------- Documents et validations ---------- */

  const cmValidated = toBooleanFlag(dossier.validate_cm);
  const reglementValidated = toBooleanFlag(dossier.validate_reglement);

  const canGenerateMemberCard = reglementValidated;

  /*
   * On conserve uniquement la licence-assurance finale.
   * Aucun formulaire de licence à signer ou signé n’est utilisé ici.
   */
  const licenceAssuranceUrl =
    dossier.licence_url ||
    dossier.licence_assurance_url ||
    dossier.licence_assurance ||
    "";

  const licenceAssuranceProvided = Boolean(licenceAssuranceUrl);

  const docsProvided =
    (dossier.cm_url ? 1 : 0) +
    (licenceAssuranceProvided ? 1 : 0);

  /* ---------- Badges ---------- */

  const getStatusPillClass = (status) => {
    switch (status) {
      case "Licencié":
        return "bg-green-300 text-slate-800 ring-1 ring-green-300/70";

      case "Licencié Hors Club":
        return "bg-green-200 text-slate-800 ring-1 ring-green-200/70";

      case "Inscrit":
        return "bg-yellow-300 text-slate-900 ring-1 ring-yellow-300/70";

      case "Cours d'essai":
        return "bg-red-300 text-slate-900 ring-1 ring-red-300/70";

      case "Licencié - Arrêt":
      case "Ancien Licencié et Arrêt":
        return "bg-slate-300 text-slate-800 ring-1 ring-slate-300/70";

      case "Ancien Licencié":
        return "bg-slate-900 text-white ring-1 ring-slate-900/50";

      case "Non Licencié - Arrêt":
        return "bg-slate-500 text-white ring-1 ring-slate-500/70";

      default:
        return "bg-slate-200 text-slate-800 ring-1 ring-slate-200/70";
    }
  };

  const docsBadgeClass =
    docsProvided === 2
      ? "bg-emerald-200 text-emerald-900 ring-1 ring-emerald-300"
      : docsProvided === 1
        ? "bg-amber-200 text-amber-900 ring-1 ring-amber-300"
        : "bg-slate-200 text-slate-800 ring-1 ring-slate-300";

  /* ---------- Progression ---------- */

  /*
   * La licence-assurance finale est un document mis à disposition.
   * Elle ne bloque pas la progression du dossier.
   */
  const steps = [
    {
      key: "validate_cm",
      label: "Médical",
      done: cmValidated,
      icon: FaFileMedical,
    },
    {
      key: "validate_reglement",
      label: "Règlement",
      done: reglementValidated,
      icon: FaEuroSign,
    },
  ];

  const doneCount = steps.filter((step) => step.done).length;
  const percent = Math.round((doneCount / steps.length) * 100);

  /* ---------- Actualisation ---------- */

  const handleRefreshDossier = async () => {
    if (typeof refreshDossier === "function") {
      await refreshDossier(adherent.id);
    } else {
      await refreshAdherents?.();
    }

    await refetchReglement();
  };

  /* ---------- Modification du type de règlement ---------- */

  const onSaveType = async () => {
    if (!reglement?.id) return;

    try {
      setSavingType(true);

      await updateReglementInscription({
        id: reglement.id,
        type_reglement: typeReglementLocal || null,
      });

      await handleRefreshDossier();

      setEditingType(false);

      toast.success("Type de règlement mis à jour.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour du type de règlement.");
    } finally {
      setSavingType(false);
    }
  };

  /* ---------- Ouverture des documents ---------- */

  const openUrl = (url) => {
    if (!url) return;

    window.open(
      toPublicUrl(url),
      "_blank",
      "noopener,noreferrer",
    );
  };

  /* ---------- Styles statut règlement ---------- */

  const statusChip = (status) => {
    const statusClasses = {
      "Non Réglé": "bg-rose-600 text-white",
      Réglé: "bg-emerald-600 text-white",
      "En cours": "bg-yellow-500 text-black",
      "En Attente": "bg-amber-300 text-slate-900",
      Annulé: "bg-slate-900 text-white",
      Remboursé: "bg-violet-600 text-white",
      "Partiellement Réglé": "bg-emerald-200 text-slate-900",
    };

    return statusClasses[status] || "bg-slate-200 text-slate-900";
  };

  /* ---------- Carte membre ---------- */

  const handleGoMemberCard = () => {
    if (!canGenerateMemberCard) return;

    navigate("/dashboard/carte-membre", {
      state: {
        adherent,
        dossier,
        settings: settings || {},
        activeSeason:
          activeSeason ||
          settings?.saison_active ||
          adherent?.saison_active ||
          "",
      },
    });
  };

  return (
    <div className="overflow-hidden rounded-2xl bg-white/90 shadow ring-1 ring-slate-200/70 dark:bg-slate-900/70 dark:ring-slate-700/70">
      {/* En-tête */}
      <div className="bg-gradient-to-r from-slate-50 to-white p-4 dark:from-slate-900/40 dark:to-slate-900/40 sm:p-5">
        <div className="flex items-center gap-4">
          <img
            src={toPublicUrl(adherent.photo_url) || defaultImageUrl}
            alt={`${adherent.nom} ${adherent.prenom}`}
            className="h-16 w-16 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700 sm:h-20 sm:w-20"
          />

          <div className="min-w-0">
            <div className="truncate text-lg font-semibold text-slate-900 dark:text-slate-100 sm:text-xl">
              {(adherent.nom || "").toUpperCase()} {adherent.prenom || ""}
            </div>

            {/* Badges */}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusPillClass(
                  adherent.statut_inscription,
                )}`}
              >
                {adherent.statut_inscription || "—"}
              </span>

              <span className="inline-flex items-center rounded-full bg-emerald-200 px-2.5 py-0.5 text-xs font-medium text-emerald-900 ring-1 ring-emerald-300">
                <FaCalendar className="mr-1" />
                {adherent.saison_active || "—"}
              </span>

              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${docsBadgeClass}`}
              >
                Documents : {docsProvided}/2
              </span>

              <button
                type="button"
                onClick={() => setIsDocsOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                title="Ajouter ou modifier les documents"
              >
                <FaPlusCircle />
                Ajouter / Modifier les documents
              </button>
            </div>

            {/* Métadonnées */}
            <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-600 dark:text-slate-300 sm:text-sm">
              <div className="flex items-center gap-2">
                <FaUserCircle />
                Licence : {adherent.licence_number || "—"}
              </div>

              <div className="flex items-center gap-2">
                <FaUsers />
                Inscrit(e) le :{" "}
                {adherent.createdAt
                  ? new Date(adherent.createdAt).toLocaleDateString()
                  : "—"}
              </div>
            </div>
          </div>
        </div>

        {/* Barre de progression */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-300">
            <span>Avancement du dossier</span>
            <span>{percent}%</span>
          </div>

          <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
            <div
              className="h-2 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500"
              style={{ width: `${percent}%` }}
            />
          </div>

          <div className="mt-2 flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
            {steps.map((step) => {
              const Icon = step.icon;

              return (
                <div key={step.key} className="flex items-center gap-1.5">
                  <Icon
                    className={
                      step.done ? "text-green-500" : "text-slate-400"
                    }
                  />

                  <span>{step.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Information */}
      <div className="px-4 pt-4 sm:px-5">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Le dossier comprend le document médical, la licence-assurance finale
          et le règlement. La validation est effectuée par le bureau.
        </p>
      </div>

      {/* Sections */}
      <div className="p-4 sm:p-5">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Médical */}
          <Section
            title="Partie médicale"
            icon={<FaFileMedical />}
            validated={cmValidated}
          >
            <div className="grid grid-cols-1 gap-3">
              <BigDocButton
                url={dossier.cm_url}
                onOpen={() => openUrl(dossier.cm_url)}
                label="Certificat / Attestation"
              />

              <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <FaCalendarAlt
                  className={
                    dossier.date_cm
                      ? "text-emerald-600"
                      : "text-slate-400"
                  }
                />

                {dossier.date_cm ? (
                  <span>
                    Date du document :{" "}
                    {new Date(dossier.date_cm).toLocaleDateString("fr-FR")}
                  </span>
                ) : (
                  <span className="text-slate-500">
                    Date du document non renseignée
                  </span>
                )}
              </div>
            </div>
          </Section>

          {/* Licence-assurance finale */}
          <Section
            title="Licence-assurance finale"
            icon={<FaFileSignature />}
            validated={licenceAssuranceProvided}
          >
            <BigDocButton
              url={licenceAssuranceUrl}
              onOpen={() => openUrl(licenceAssuranceUrl)}
              label="Licence / Assurance"
            />

            <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
              Document définitif de licence-assurance.
            </p>
          </Section>

          {/* Règlement */}
          <Section
            title="Règlement"
            icon={<FaEuroSign />}
            validated={reglementValidated}
          >
            <div className="text-sm text-slate-700 dark:text-slate-300">
              {reglement ? (
                <>
                  <div className="flex items-center gap-2">
                    <span>Statut :</span>

                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusChip(
                        reglement.statut_reglement || "",
                      )}`}
                    >
                      {reglement.statut_reglement || "—"}
                    </span>
                  </div>

                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span>Type :</span>

                    {!editingType ? (
                      <>
                        <span className="font-medium">
                          {reglement.type_reglement || "—"}
                        </span>

                        <button
                          type="button"
                          onClick={() => {
                            setTypeReglementLocal(
                              reglement.type_reglement || "",
                            );
                            setEditingType(true);
                          }}
                          className="ml-2 inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium ring-1 ring-slate-300 hover:bg-slate-50 dark:ring-slate-700 dark:hover:bg-slate-800"
                        >
                          Modifier
                        </button>
                      </>
                    ) : (
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <select
                          value={typeReglementLocal}
                          onChange={(event) =>
                            setTypeReglementLocal(event.target.value)
                          }
                          disabled={savingType}
                          className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900"
                        >
                          <option value="">— Sélectionner —</option>
                          <option value="En ligne">En ligne</option>
                          <option value="Chèque">Chèque</option>
                          <option value="Espèces">Espèces</option>
                          <option value="Virement">Virement</option>
                          <option value="CB">CB</option>
                          <option value="Autre">Autre</option>
                        </select>

                        <button
                          type="button"
                          onClick={onSaveType}
                          disabled={savingType}
                          className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                        >
                          {savingType
                            ? "Enregistrement…"
                            : "Enregistrer"}
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setEditingType(false);
                            setTypeReglementLocal(
                              reglement.type_reglement || "",
                            );
                          }}
                          disabled={savingType}
                          className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-300 hover:bg-slate-50 dark:text-slate-100 dark:ring-slate-700 dark:hover:bg-slate-800"
                        >
                          Annuler
                        </button>
                      </div>
                    )}
                  </div>

                  {(reglement.reduction_foyer ||
                    reglement.pass_sport ||
                    reglement.reduction_saison ||
                    reglement.montant_lic_assurance ||
                    reglement.montant_cotisation) && (
                    <div className="mt-2 text-xs">
                      <div className="font-medium underline">
                        Licence et cotisation :
                      </div>

                      <div>
                        Licence FFJDA :{" "}
                        {reglement.montant_lic_assurance || 0} €
                      </div>

                      <div>
                        Cotisation club :{" "}
                        {reglement.montant_cotisation || 0} €
                      </div>

                      <div className="mt-1 font-medium underline">
                        Réductions :
                      </div>

                      <div>
                        Réduction foyer :{" "}
                        {reglement.reduction_foyer || 0} €
                      </div>

                      <div>
                        Pass’Sport : {reglement.pass_sport || 0} €
                      </div>

                      <div>
                        Réduction saison :{" "}
                        {reglement.reduction_saison || 0} €
                      </div>
                    </div>
                  )}

                  <div className="mt-2 text-xs font-medium underline">
                    Total :
                  </div>

                  <div>
                    Montant total :{" "}
                    <span className="font-semibold">
                      {Number(reglement.montant_cotisation || 0) +
                        Number(reglement.montant_lic_assurance || 0) +
                        Number(reglement.reduction_foyer || 0) +
                        Number(reglement.pass_sport || 0) +
                        Number(reglement.reduction_saison || 0)}{" "}
                      €
                    </span>
                  </div>

                  <div className="mt-2 text-xs text-red-500">
                    Le règlement de la licence-cotisation s’effectue sur la page
                    Commandes & Règlements.
                  </div>

                  <div className="mt-2 text-xs text-red-500">
                    L’attestation d’inscription est téléchargeable une fois le
                    règlement validé.
                  </div>
                </>
              ) : (
                <span className="opacity-70">
                  Aucun règlement enregistré pour le moment.
                </span>
              )}
            </div>

            {(!reglement ||
              reglement.statut_reglement?.toLowerCase() !== "réglé") && (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-slate-500">
                  Finalisez votre paiement depuis l’espace règlements.
                </span>

                <button
                  type="button"
                  onClick={() => navigate("/commandes-&-reglements")}
                  className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                >
                  Accéder aux règlements
                </button>
              </div>
            )}
          </Section>
        </div>

        {/* Actions */}
        <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {adherent.statut_inscription === "Cours d'essai" && (
            <button
              type="button"
              onClick={() => setIsTrialOpen(true)}
              className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <FaThumbsUp className="mr-2" />
              Validation du cours d’essai ?
            </button>
          )}

          {dossier.lien_drive && (
            <>
              <button
                type="button"
                onClick={() =>
                  window.open(
                    dossier.lien_drive,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                className="inline-flex items-center justify-center rounded-xl bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700"
              >
                <FaGoogleDrive className="mr-2" />
                Déposer dans Google Drive
              </button>

              <button
                type="button"
                onClick={async () => {
                  try {
                    const notification =
                      `${user.prenom} ${user.nom} a déposé des documents ` +
                      `pour ${adherent.prenom} ${adherent.nom} dans Google Drive.`;

                    await sendNotificationToAdmin(
                      notification,
                      user.token,
                    );

                    toast.success(
                      "Notification envoyée aux administrateurs.",
                    );
                  } catch (error) {
                    console.error(error);
                    toast.error(
                      "Erreur lors de l’envoi de la notification.",
                    );
                  }
                }}
                className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                <FaBell className="mr-2" />
                Signaler le dépôt
              </button>
            </>
          )}

          {/* Carte membre */}
          <button
            type="button"
            onClick={handleGoMemberCard}
            disabled={!canGenerateMemberCard}
            title={
              canGenerateMemberCard
                ? "Créer la carte de membre"
                : "Disponible uniquement lorsque le règlement est validé"
            }
            className={`inline-flex items-center justify-center rounded-xl px-3 py-2 text-sm font-medium transition ${
              canGenerateMemberCard
                ? "bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
                : "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500"
            }`}
          >
            {canGenerateMemberCard ? (
              <FaIdCard className="mr-2" />
            ) : (
              <FaLock className="mr-2" />
            )}

            Carte membre
          </button>

          {/* Attestation d’inscription */}
          {reglementValidated && (
            <button
              type="button"
              onClick={() => {
                navigate("/attestation", {
                  state: {
                    prenom: adherent.prenom,
                    nom: adherent.nom,
                    dateNaissance: adherent.date_naissance
                      ? new Date(
                          adherent.date_naissance,
                        ).toLocaleDateString("fr-FR")
                      : "",
                    adresse: adherent.adresse,
                    cp: adherent.cp,
                    commune: adherent.commune,
                    cotisation: reglement?.montant_cotisation,
                    licence: reglement?.montant_lic_assurance,
                    reductionFoyer: reglement?.reduction_foyer,
                    passSport: reglement?.pass_sport,
                    reductionSaison: reglement?.reduction_saison,
                    typeReglement: reglement?.type_reglement,
                    nbReglement: reglement?.nb_reglement,
                    statutReglement: reglement?.statut_reglement,
                    observations: reglement?.observations,
                    datePaiement: new Date().toLocaleDateString("fr-FR"),
                    montant:
                      Number(reglement?.montant_cotisation || 0) +
                      Number(reglement?.montant_lic_assurance || 0),
                    president: settings?.responsable_club,
                    association: settings?.nom_club,
                    logo: settings?.logo_club,
                    adresse_club: settings?.adresse_club,
                    tel_club: settings?.tel_club,
                    mail_club: settings?.mail_club,
                    siteweb_club: settings?.siteweb_club,
                    siret_club: settings?.siret_club,
                    saison_active: activeSeason,
                  },
                });
              }}
              className="inline-flex items-center justify-center rounded-xl bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
            >
              <FaFilePdf className="mr-2" />
              Attestation d’inscription
            </button>
          )}
        </div>
      </div>

      {/* Modales */}
      <ValidateTrialModal
        isOpen={isTrialOpen}
        onClose={async (updated) => {
          setIsTrialOpen(false);

          if (updated) {
            await handleRefreshDossier();
          }
        }}
        adherent={adherent}
        user={user}
        currentGroupIds={
          Array.isArray(adherent.group_ids)
            ? adherent.group_ids
            : adherent.group_id
              ? [adherent.group_id]
              : undefined
        }
      />

      <EditDocumentsModal
        isOpen={isDocsOpen}
        onClose={() => setIsDocsOpen(false)}
        dossier={dossier}
        refreshDossiers={handleRefreshDossier}
      />
    </div>
  );
};

export default AdherentCard;